// ============================================================================
// L'ARBRE DES ARTICLES RAPIDES — ce que la caisse DESSINE d'un niveau, et ce que l'aperçu du studio doit montrer.
//
// POURQUOI ICI. Le studio du back-office promet « ce que vous voyez est ce que la caisse affiche » ; la caisse
// dessine la vraie grille. Écrite deux fois, la règle « quelles tuiles apparaissent, combien un dossier en annonce »
// divergerait au premier correctif, et l'aperçu montrerait un autre écran que celui que le caissier touche. Une règle
// que deux surfaces doivent lire pareil EST le contrat (§24.3bis).
// ⚠️ L'ORDRE N'EST PAS ICI : le back-office range les tuiles dans l'ordre de son document, la caisse par
//    `sort_order` — et peut surcharger au doigt. Chacun passe le niveau déjà ordonné. Seul le niveau d'une SOURCE
//    se range ici, parce que ses articles ne sont pas des tuiles : il faut une règle pour les mêler aux tuiles posées.
// ============================================================================

/** Les trois SOURCES : une tuile qui déverse en direct les articles d'une famille, d'une sous-famille ou d'une marque. */
export type QuickSourceKind = 'family' | 'sub_family' | 'brand';

/**
 * Les trois PRÉRÉGLAGES STRUCTURELS : une tuile qui ouvre un niveau d'UNE TUILE PAR VALEUR du référentiel — toutes les
 * familles, sous-familles ou marques qui déversent au moins un article vendable.
 */
export type QuickPresetKind = 'all_families' | 'all_sub_families' | 'all_brands';

/** Les natures de tuile : un dossier, un article, une case vide qui aligne ou sépare — les trois sources et les trois préréglages. */
export type QuickTileKind = 'folder' | 'article' | 'empty' | QuickSourceKind | QuickPresetKind;

/** Les sources, dans l'ordre de la maquette. */
export const QUICK_SOURCE_KINDS: readonly QuickSourceKind[] = ['family', 'sub_family', 'brand'];

/** Les préréglages structurels, dans l'ordre de la maquette (« Toutes les familles », « … sous-familles », « … marques »). */
export const QUICK_PRESET_KINDS: readonly QuickPresetKind[] = ['all_families', 'all_sub_families', 'all_brands'];

/** La source que chaque préréglage déploie — une tuile par valeur de ce référentiel. */
export const PRESET_SOURCE: Readonly<Record<QuickPresetKind, QuickSourceKind>> = {
  all_families: 'family', all_sub_families: 'sub_family', all_brands: 'brand',
};

/**
 * LES NATURES QUE CETTE VERSION SAIT DESSINER.
 * ⚠️ Une nature absente d'ici — venue d'un serveur plus récent que l'application — ne se dessine pas : dessinée
 *    comme un article, elle donnerait un bouton dont le toucher ne fait rien.
 */
export const QUICK_TILE_KINDS: readonly QuickTileKind[] = ['folder', 'article', 'empty', ...QUICK_SOURCE_KINDS, ...QUICK_PRESET_KINDS];

/** Une source : une famille, une sous-famille ou une marque. */
export function isSourceKind(kind: string): kind is QuickSourceKind {
  return (QUICK_SOURCE_KINDS as readonly string[]).includes(kind);
}

/** Un préréglage structurel. */
export function isPresetKind(kind: string): kind is QuickPresetKind {
  return (QUICK_PRESET_KINDS as readonly string[]).includes(kind);
}

/** Une tuile qui OUVRE un niveau au toucher : un dossier, une source, ou un préréglage. */
export function opensLevel(kind: string): boolean {
  return kind === 'folder' || isSourceKind(kind) || isPresetKind(kind);
}

// ── Les PRÉRÉGLAGES : des tuiles déployées, jamais stockées ─────────────────────────────────────

/**
 * L'IDENTIFIANT D'UNE TUILE DÉPLOYÉE par un préréglage — celui du préréglage et celui de la valeur.
 * ⚠️ `~` NE PEUT FIGURER DANS AUCUN IDENTIFIANT DE TUILE STOCKÉE (le serveur les refuse) : une tuile déployée ne se
 *    confond donc jamais avec une tuile posée, et ne peut pas être enregistrée comme parent ni publiée.
 */
export const PRESET_CHILD_MARK = '~';
export const presetChildId = (presetId: string, valueId: string): string => `${presetId}${PRESET_CHILD_MARK}${valueId}`;
export const isPresetChildId = (id: string): boolean => id.includes(PRESET_CHILD_MARK);

/** Ce que le déploiement lit d'une tuile. */
export interface QuickPresetNode extends QuickTileNode {
  parentId: string | null;
  /** Pour un préréglage : les valeurs que le studio a retirées (identifiants de valeur). */
  hidden: readonly string[];
}

/**
 * DÉPLOIE LES PRÉRÉGLAGES — à chaque préréglage, une tuile SOURCE par valeur de son référentiel, après ses tuiles posées.
 * - `values(kind)` : les valeurs qui déversent au moins un article vendable, DÉJÀ RANGÉES (`sortByLabel` de
 *   `core-catalog`) — chaque surface les lit dans SA base, la règle de l'ordre est la même ;
 * - une valeur RETIRÉE (`hidden`) ne se déploie pas ; une valeur déjà POSÉE dans le niveau (une tuile source de même
 *   valeur) non plus — on ne la montre pas deux fois ;
 * - `make` fabrique la tuile de la surface : la source, son identifiant (`presetChildId`), et ce qu'elle hérite du
 *   préréglage (son habillage).
 * ⚠️ LES TUILES DÉPLOYÉES NE S'ENREGISTRENT JAMAIS : une famille ajoutée demain au référentiel paraît d'elle-même, une
 *    famille supprimée disparaît — c'est tout l'intérêt d'un préréglage.
 */
export function expandPresets<T extends QuickPresetNode & { sourceId?: string | null }>(
  tiles: readonly T[],
  values: (kind: QuickSourceKind) => readonly { id: string }[],
  make: (preset: T, kind: QuickSourceKind, valueId: string, childId: string) => T,
): T[] {
  const out = [...tiles];
  for (const preset of tiles) {
    if (!isPresetKind(preset.kind)) continue;
    const kind = PRESET_SOURCE[preset.kind];
    const masked = new Set(preset.hidden);
    const placed = new Set(tiles.filter((t) => t.parentId === preset.id && t.kind === kind && t.sourceId).map((t) => t.sourceId!));
    for (const v of values(kind)) {
      if (masked.has(v.id) || placed.has(v.id)) continue;
      out.push(make(preset, kind, v.id, presetChildId(preset.id, v.id)));
    }
  }
  return out;
}

// ── L'habillage d'une tuile ─────────────────────────────────────────────────────────────────────

/**
 * LES TEINTES D'UNE TUILE — des CLÉS de la charte, jamais un hexadécimal : la tuile suit le thème clair ou sombre, et
 * une retouche de la charte atteint toutes les tuiles sans rien réécrire. `ink` est la teinte neutre.
 */
export const QUICK_TILE_COLORS = ['teal', 'amber', 'rose', 'violet', 'blue', 'green', 'orange', 'ink'] as const;
export type QuickTileColor = (typeof QUICK_TILE_COLORS)[number];
export function isQuickTileColor(v: unknown): v is QuickTileColor {
  return typeof v === 'string' && (QUICK_TILE_COLORS as readonly string[]).includes(v);
}

/**
 * CE QU'UNE TUILE MONTRE AU-DESSUS DE SON NOM (maquette `viss`) : rien, un picto, un grand picto, une photo.
 * ⚠️ ABSENT (`null`), C'EST LE RENDU PAR DÉFAUT DE LA CAISSE — la vignette d'un article, le nom d'un dossier — et non
 *    « rien » : une tuile composée avant l'habillage ne change pas d'aspect à la publication suivante.
 */
export const QUICK_TILE_VISUALS = ['none', 'icon', 'large_icon', 'photo'] as const;
export type QuickTileVisual = (typeof QUICK_TILE_VISUALS)[number];
export function isQuickTileVisual(v: unknown): v is QuickTileVisual {
  return typeof v === 'string' && (QUICK_TILE_VISUALS as readonly string[]).includes(v);
}

/**
 * LA TAILLE D'UNE PHOTO DE TUILE, pour toute tuile qui s'habille : `normal` (le bandeau 16:10 d'une vignette, la photo
 * entière d'un dossier), `reduced` (le bandeau divisé par deux — sous lui, le texte d'un article, ou le nom et la
 * pastille d'un dossier), `left` (un caisson carré à gauche du nom).
 * ⚠️ ABSENTE (`null`), C'EST `normal` : une tuile composée avant les tailles ne change pas d'aspect.
 * ⚠️ Une caisse d'avant la photo réduite des dossiers montre la photo entière d'un dossier « réduit » : elle ne refuse
 *    rien, elle dessine ce qu'elle sait.
 */
export const QUICK_PHOTO_SIZES = ['normal', 'reduced', 'left'] as const;
export type QuickPhotoSize = (typeof QUICK_PHOTO_SIZES)[number];
export function isQuickPhotoSize(v: unknown): v is QuickPhotoSize {
  return typeof v === 'string' && (QUICK_PHOTO_SIZES as readonly string[]).includes(v);
}

/** L'HABILLAGE D'UNE TUILE, champ par champ — `null` partout : non habillée. */
export interface QuickTileLook {
  color: QuickTileColor | null;
  visual: QuickTileVisual | null;
  icon: string | null;
  photoSize: QuickPhotoSize | null;
}

/**
 * L'HABILLAGE D'UN ARTICLE DANS LE NIVEAU D'UNE SOURCE — le sien, CHAMP PAR CHAMP, sinon celui que la source donne à ses
 * articles (« Ses articles », réglé sur la tuile de la famille, de la sous-famille ou de la marque).
 * ⚠️ UNE RÈGLE, PAS UNE COPIE : un article entré demain dans la famille prend la forme des autres sans qu'on écrive rien,
 *    et l'article retouché à part (un champ posé sur sa tuile) garde son choix pour ce champ seulement.
 * - `own` : la tuile de l'article, `null` pour un article déversé ; `fromSource` : l'habillage des articles de la source.
 */
export function inheritedLook(own: QuickTileLook | null, fromSource: QuickTileLook | null): QuickTileLook {
  return {
    color: own?.color ?? fromSource?.color ?? null,
    visual: own?.visual ?? fromSource?.visual ?? null,
    icon: own?.icon ?? fromSource?.icon ?? null,
    photoSize: own?.photoSize ?? fromSource?.photoSize ?? null,
  };
}

/**
 * LES VISUELS QU'UNE DISPOSITION DONNE À SES TUILES — celui de tout l'écran (« Appliquer à toutes les tuiles… ») et
 * celui de tous ses articles (« Aux articles seulement… »). `null` : rien de posé.
 */
export interface QuickScreenVisuals { visual: QuickTileVisual | null; articlesVisual: QuickTileVisual | null }

export const NO_SCREEN_VISUALS: QuickScreenVisuals = { visual: null, articlesVisual: null };

/**
 * LE VISUEL D'UN ÉCRAN QUI N'EN A AUCUN DE POSÉ — « Petit picto » (maquette du studio, `defVis`) : une tuile sans visuel
 * à elle, ni hérité, ni d'écran, est un carré à petit picto. C'est aussi le visuel des nouvelles tuiles quand rien
 * n'est choisi.
 */
export const QUICK_SCREEN_DEFAULT_VISUAL: QuickTileVisual = 'icon';

/**
 * L'HABILLAGE D'UNE TUILE DANS SA DISPOSITION — le sien, champ par champ ; pour le VISUEL, sinon celui de tous les
 * articles de la disposition (un article seulement), sinon celui que sa source donne à ses articles, sinon celui de
 * tout l'écran, sinon « Petit picto » (`QUICK_SCREEN_DEFAULT_VISUAL`) — une tuile de l'arbre a TOUJOURS un visuel.
 * ⚠️ LE VISUEL DES ARTICLES PASSE AVANT CELUI D'UNE SOURCE : « Aux articles seulement » vise TOUS les articles de
 *    l'écran, déversés compris — c'est le geste qui l'a posé qui l'a annoncé ainsi.
 * ⚠️ UNE RÈGLE, TROIS LECTEURS : le composeur, l'aperçu et la caisse la lisent ici.
 */
export function screenLook(kind: QuickTileKind, own: QuickTileLook | null, fromSource: QuickTileLook | null, screen: QuickScreenVisuals): QuickTileLook & { visual: QuickTileVisual } {
  const look = inheritedLook(own, fromSource);
  return {
    ...look,
    visual: own?.visual ?? (kind === 'article' ? screen.articlesVisual : null) ?? fromSource?.visual ?? screen.visual ?? QUICK_SCREEN_DEFAULT_VISUAL,
  };
}

/**
 * LE NOM D'UN PICTO — sa FORME seulement. Le registre des icônes vit dans la charte (`ui-tokens`), que le contrat ne
 * lit pas.
 * ⚠️ UN NOM QUE LE RENDU NE CONNAÎT PAS DESSINE LE PICTO PAR DÉFAUT (`QUICK_TILE_DEFAULT_ICON`), jamais un vide : venu
 *    d'un serveur plus récent ou d'une icône retirée de la charte, il ne doit pas laisser une tuile sans visuel.
 */
export const QUICK_TILE_ICON = /^(?=.{1,40}$)[a-z][a-zA-Z0-9]*(-[a-z0-9][a-zA-Z0-9]*)*$/;
export const QUICK_TILE_DEFAULT_ICON = 'tag';

/**
 * LE CADRAGE D'UNE PHOTO DE TUILE — point focal en centièmes de pourcent (0 à 10 000, entiers : pas de flottant en
 * base), zoom en centièmes (100 = 1×, jusqu'à 300 = 3×, la borne de la maquette).
 */
export const QUICK_PHOTO_FOCAL_MAX = 10_000;
export const QUICK_PHOTO_ZOOM_MIN = 100;
export const QUICK_PHOTO_ZOOM_MAX = 300;

/**
 * LA PHOTO QUE LA CAISSE MONTRE SUR UNE TUILE QUI MONTRE UNE PHOTO — celle du COMPTOIR d'abord (décision utilisateur) :
 * la photo prise sur une tablette du magasin est la plus proche de ce qui est en rayon. Puis la photo du studio, puis le
 * visuel de la FICHE (« en dernier recours », décision utilisateur). Pour un dossier ou une source, seule celle du
 * studio existe.
 */
export function tilePhotoChoice(
  counter: string | null | undefined, studio: string | null | undefined, catalog: string | null | undefined = null,
): { uri: string; from: 'counter' | 'studio' | 'catalog' } | null {
  if (counter) return { uri: counter, from: 'counter' };
  if (studio) return { uri: studio, from: 'studio' };
  if (catalog) return { uri: catalog, from: 'catalog' };
  return null;
}

/** Une photo de fiche telle que la base la garde, réduite à ce qui choisit la photo d'une tuile. */
export interface QuickCatalogPhoto {
  color: string | null;
  rank: number;
  url: string | null;
  thumbUrl: string | null;
  isMain: boolean;
  origin: string | null;
}

/**
 * LES PHOTOS D'UN ARTICLE POUR SA TUILE — parmi celles de son MODÈLE, pour SON coloris : la photo du COMPTOIR (prise sur
 * une caisse du magasin) et le visuel de FICHE qui sert en dernier recours.
 * - comptoir : la photo de provenance `counter` de son coloris ;
 * - fiche : la première photo de son coloris, sinon le visuel du modèle (sans coloris), sinon la photo du modèle ;
 * - la VIGNETTE quand elle existe : une tuile fait 150 px, l'image pleine en fait 1 600.
 * ⚠️ UNE RÈGLE, DEUX LECTEURS : l'aperçu du studio et la caisse la lisent ici — écrite deux fois, l'aperçu montrerait
 *    une autre photo que la caisse.
 */
export function articleTilePhotos(photos: readonly QuickCatalogPhoto[], color: string | null): { counter: string | null; catalog: string | null } {
  const shown = (p: QuickCatalogPhoto | undefined): string | null => (p ? p.thumbUrl ?? p.url : null);
  const byRank = (list: QuickCatalogPhoto[]) => [...list].sort((a, b) => a.rank - b.rank);
  const mine = byRank(photos.filter((p) => p.color === color && (p.url || p.thumbUrl)));
  const counter = shown(mine.find((p) => p.origin === 'counter'));
  const catalog = shown(mine[0])
    ?? shown(byRank(photos.filter((p) => p.color === null && (p.url || p.thumbUrl)))[0])
    ?? shown(photos.find((p) => p.isMain && (p.url || p.thumbUrl)));
  return { counter, catalog };
}

/**
 * OÙ SE POSE UNE PHOTO CADRÉE DANS SA CASE — la géométrie que le navigateur applique (`background-size: cover`,
 * `background-position: x% y%`, puis `transform: scale(zoom)` depuis ce même point), écrite pour qu'une caisse sans
 * CSS place l'image au même pixel.
 * ⚠️ UNE SEULE ÉCRITURE DU CADRAGE : la caisse en dépend, et un cadrage à peu près pareil montrerait en caisse un
 *    autre recadrage que celui choisi au studio. Le banc la confronte aux règles du CSS.
 * - `box`, `image` : tailles en pixels ; `framing` : point focal en centièmes de pourcent, zoom en centièmes.
 */
export function photoFrame(
  box: { w: number; h: number }, image: { w: number; h: number }, framing: { x: number; y: number; zoom: number },
): { left: number; top: number; width: number; height: number } {
  const px = framing.x / QUICK_PHOTO_FOCAL_MAX;
  const py = framing.y / QUICK_PHOTO_FOCAL_MAX;
  const zoom = framing.zoom / 100;
  const cover = Math.max(box.w / image.w, box.h / image.h);
  const w0 = image.w * cover;
  const h0 = image.h * cover;
  // `background-position: p%` aligne le point p% de l'image sur le point p% de la case.
  const left0 = (box.w - w0) * px;
  const top0 = (box.h - h0) * py;
  // `scale` depuis le point focal de la case : chaque point s'éloigne de ce centre d'un facteur `zoom`.
  const ox = box.w * px;
  const oy = box.h * py;
  return { left: ox + zoom * (left0 - ox), top: oy + zoom * (top0 - oy), width: w0 * zoom, height: h0 * zoom };
}

/**
 * CE QU'UNE TUILE MONTRE — la règle que le composeur, l'aperçu et la caisse appliquent ensemble.
 *
 * · `default` : le rendu d'avant l'habillage — un dossier compact, un article en vignette (des hachures sans photo), à
 *   sa taille ; · `none` : le nom seul · `icon` : le picto (grand ou non) · `photo` : une image, d'où elle vient, à sa
 *   taille.
 *
 * ⚠️ LA PHOTO DU COMPTOIR NE VAUT QUE POUR UNE TUILE QUI MONTRE UNE PHOTO (décision utilisateur) : une
 *    tuile « Photo » ou un article non habillé. « Texte seul » et « Picto » sont des choix, qu'une photo prise au
 *    comptoir ne défait pas — elle va sur la fiche de l'article, et se montre partout où l'on a choisi une photo.
 * ⚠️ UNE PHOTO DEMANDÉE MAIS ABSENTE RETOMBE SUR LE PICTO, jamais sur un cadre vide : chacun passe ce qu'il SAIT
 *    montrer (`studio` = `null` quand le rendu n'a pas l'image), et la règle dit le reste.
 */
export type QuickTileFace =
  | { kind: 'default'; size: QuickPhotoSize }
  | { kind: 'none' }
  | { kind: 'icon'; icon: string; large: boolean }
  | { kind: 'photo'; uri: string; from: 'counter' | 'studio' | 'catalog'; size: QuickPhotoSize };

/**
 * - `counter` : la photo du COMPTOIR de cet article (prise sur cette caisse, ou sur une autre caisse du magasin et
 *   redescendue) — elle l'emporte sur tout ;
 * - `studio` : la photo posée sur la tuile au studio ;
 * - `catalog` : le visuel de la fiche (son coloris d'abord) — en dernier recours ;
 * les trois, SEULEMENT là où une image est attendue : une tuile non habillée ou une tuile « Photo ». Un picto ou
 * « Texte seul » choisis au studio sont des choix : aucune photo ne les recouvre.
 * - `tile.photoSize` : la taille de la photo (`null` = normale).
 */
export function tileFace(tile: { kind: QuickTileKind; visual: QuickTileVisual | null; icon: string | null; photoSize?: QuickPhotoSize | null }, photos: {
  counter?: string | null; studio?: string | null; catalog?: string | null;
} = {}): QuickTileFace {
  const article = tile.kind === 'article';
  const size = tile.photoSize ?? 'normal';
  const counter = article ? photos.counter ?? null : null;
  const catalog = article ? photos.catalog ?? null : null;
  const icon = { kind: 'icon' as const, icon: tile.icon ?? QUICK_TILE_DEFAULT_ICON, large: tile.visual === 'large_icon' };
  switch (tile.visual) {
    case 'none': return { kind: 'none' };
    case 'icon': case 'large_icon': return icon;
    case 'photo': {
      const p = tilePhotoChoice(counter, photos.studio, catalog);
      return p ? { kind: 'photo', ...p, size } : icon;
    }
    default: {
      // Non habillée : un article montre sa photo (comptoir, sinon fiche) ou des hachures à sa taille, un dossier son nom.
      const p = article ? tilePhotoChoice(counter, null, catalog) : null;
      return p ? { kind: 'photo', ...p, size } : { kind: 'default', size };
    }
  }
}

/**
 * LA BORNE D'UNE SOURCE (A3) : une famille déverse ses DÉCLINAISONS, et quarante modèles en font des centaines — au-delà,
 * un niveau ne se parcourt plus au doigt. La liste est donc bornée, et le nombre de celles qui ne s'affichent pas est DIT.
 */
export const QUICK_POUR_MAX = 200;

/** Ce que ces règles lisent d'une tuile — le reste (libellé, encombrement, ordre) appartient à chaque surface. */
export interface QuickTileNode {
  id: string;
  kind: QuickTileKind;
  /** L'article vendu — renseigné pour un article seulement. */
  articleId: string | null;
}

/** Une nature que cette version sait dessiner. */
const known = (kind: string): boolean => (QUICK_TILE_KINDS as readonly string[]).includes(kind);

/**
 * LES TUILES QU'UN NIVEAU DESSINE, dans l'ordre reçu — sans les articles que la caisse ne vend pas, ni les natures
 * qu'elle ne connaît pas.
 * ⚠️ Un article désactivé, ou absent du catalogue de la caisse, ne se dessine pas : le toucher n'ajouterait rien au
 *    ticket. Le studio le montre « Pas en caisse » dans son composeur ; son APERÇU, lui, doit s'en passer.
 */
export function drawnTiles<T extends QuickTileNode>(level: readonly T[], sellable: (articleId: string) => boolean): T[] {
  return level.filter((t) => known(t.kind) && (t.kind !== 'article' || (t.articleId !== null && sellable(t.articleId))));
}

/**
 * LE NOMBRE QU'ANNONCE UN DOSSIER — ce qu'il dessine et qu'on peut toucher : ni ses cases vides, ni ses articles
 * qui ne se vendent pas. « 3 » sur la tuile et deux articles à l'ouverture seraient un écran qui ment.
 */
export function drawnCount<T extends QuickTileNode>(level: readonly T[], sellable: (articleId: string) => boolean): number {
  return drawnTiles(level, sellable).filter((t) => t.kind !== 'empty').length;
}

/**
 * LES ARTICLES QU'OUVRE UNE TUILE, TOUS NIVEAUX CONFONDUS — ce que sa pastille annonce (« 12 articles », « vide »). Un
 * article compte pour un, une case vide pour rien, une source pour ce qu'elle déverse (`sourceArticles`), un dossier ou
 * un préréglage pour la somme de son niveau.
 * ⚠️ UN COMPTE ENCORE INCONNU REND LE TOUT INCONNU (`null`) : annoncer « 3 articles » quand une source n'est pas encore
 *    lue serait un chiffre faux, affiché comme vrai. Un niveau inconnu aussi (`levelOf` rend `null` : un préréglage
 *    dont le référentiel n'est pas lu n'est pas vide). La caisse, qui connaît tout, reçoit un nombre.
 * ⚠️ UNE TUILE DÉJÀ PARCOURUE NE SE RECOMPTE PAS : un arbre abîmé qui bouclerait ne gèle pas l'écran.
 * ⚠️ UNE RÈGLE, DEUX LECTEURS : le studio et la caisse l'appliquent — écrite deux fois, la pastille de l'aperçu
 *    annoncerait un autre compte que celle de la caisse.
 */
export function deepArticleCount<T extends QuickTileNode>(
  tile: T, levelOf: (tile: T) => readonly T[], sourceArticles: (tile: T) => number, sellable: (articleId: string) => boolean,
): number;
export function deepArticleCount<T extends QuickTileNode>(
  tile: T, levelOf: (tile: T) => readonly T[] | null, sourceArticles: (tile: T) => number | null, sellable: (articleId: string) => boolean,
): number | null;
export function deepArticleCount<T extends QuickTileNode>(
  tile: T, levelOf: (tile: T) => readonly T[] | null, sourceArticles: (tile: T) => number | null, sellable: (articleId: string) => boolean,
): number | null {
  const seen = new Set<string>();
  const walk = (t: T): number | null => {
    if (seen.has(t.id)) return 0;
    seen.add(t.id);
    if (isSourceKind(t.kind)) return sourceArticles(t);
    const level = levelOf(t);
    if (level === null) return null;
    let n = 0;
    for (const child of drawnTiles(level, sellable)) {
      if (child.kind === 'article') { n += 1; continue; }
      if (!opensLevel(child.kind)) continue;
      const inner = walk(child);
      if (inner === null) return null;
      n += inner;
    }
    return n;
  };
  return walk(tile);
}

/** LES BOUTONS DU RAIL (« Menu fixe ») — l'accueil dessiné, sans ses cases vides : un rail n'aligne rien. */
export function railButtons<T extends QuickTileNode>(home: readonly T[], sellable: (articleId: string) => boolean): T[] {
  return drawnTiles(home, sellable).filter((t) => t.kind !== 'empty');
}

/**
 * LE NIVEAU OUVERT QUAND LA CAISSE ARRIVE SUR L'ÉCRAN — l'accueil, ou avec le rail la première tuile du rail qui ouvre
 * un niveau (dossier ou source).
 * ⚠️ Avec le rail, l'accueil EST le rail : la grille montre le niveau ouvert, sans quoi elle répéterait le rail. Un
 *    rail sans tuile qui s'ouvre montre l'accueil dans la grille, comme l'aperçu du studio.
 */
export function startPath<T extends QuickTileNode>(rail: boolean, home: readonly T[]): string[] {
  if (!rail) return [];
  const first = home.find((t) => known(t.kind) && opensLevel(t.kind));
  return first ? [first.id] : [];
}

// ── Le niveau d'une SOURCE ──────────────────────────────────────────────────────────────────────

/** La clé d'une tuile posée dans l'ordre retouché d'un niveau de source. */
export const tileKey = (tileId: string): string => `t:${tileId}`;
/** La clé d'un article déversé dans l'ordre retouché d'un niveau de source. */
export const articleKey = (articleId: string): string => `a:${articleId}`;

/** Une entrée d'un niveau de source : une tuile POSÉE, ou un article DÉVERSÉ par la source. */
export type QuickLevelEntry<T> =
  | { key: string; tile: T; articleId: null }
  | { key: string; tile: null; articleId: string };

/**
 * LE NIVEAU D'UNE SOURCE (famille, sous-famille, marque) — ses tuiles posées, puis les articles qu'elle déverse.
 * - `poured` : les articles de la source, dans l'ordre par défaut (`compareDeclinations` de `core-catalog`) ;
 * - un article MASQUÉ ne sort pas ; un article déjà POSÉ dans ce niveau ne sort pas une seconde fois — le retoucher en a
 *   fait une tuile à part entière, à sa place, et le remettre au catalogue revient à retirer cette tuile ;
 * - l'ORDRE RETOUCHÉ (`order`, clés `tileKey`/`articleKey`) range ce qu'il nomme ; le reste suit dans l'ordre par
 *   défaut, tuiles posées d'abord : un article entré demain dans la famille paraît en fin de niveau.
 */
export function sourceLevel<T extends QuickTileNode>(
  own: readonly T[],
  poured: readonly string[],
  hidden: readonly string[],
  order: readonly string[],
): QuickLevelEntry<T>[] {
  const masked = new Set(hidden);
  const placed = new Set(own.flatMap((t) => (t.kind === 'article' && t.articleId !== null ? [t.articleId] : [])));
  const entries: QuickLevelEntry<T>[] = [
    ...own.map((tile): QuickLevelEntry<T> => ({ key: tileKey(tile.id), tile, articleId: null })),
    ...poured
      .filter((a) => !masked.has(a) && !placed.has(a))
      .map((articleId): QuickLevelEntry<T> => ({ key: articleKey(articleId), tile: null, articleId })),
  ];
  const rank = new Map(order.map((k, i) => [k, i]));
  return entries
    .map((entry, i) => ({ entry, r: rank.get(entry.key) ?? order.length + i }))
    .sort((a, b) => a.r - b.r)
    .map((x) => x.entry);
}

/**
 * LA BORNE D'UN NIVEAU DE SOURCE — toutes ses tuiles posées, ses articles déversés jusqu'à `max`, et le nombre de ceux
 * qui restent : l'écran le DIT (A3 — jamais une coupe muette).
 */
export function boundSourceLevel<T>(entries: readonly QuickLevelEntry<T>[], max: number = QUICK_POUR_MAX): { entries: QuickLevelEntry<T>[]; beyond: number } {
  const out: QuickLevelEntry<T>[] = [];
  let shown = 0;
  let beyond = 0;
  for (const e of entries) {
    if (e.tile === null) {
      if (shown >= max) { beyond += 1; continue; }
      shown += 1;
    }
    out.push(e);
  }
  return { entries: out, beyond };
}

/**
 * UNE DÉCLINAISON QU'UNE SOURCE DÉVERSE — active, porteuse d'un code-barres (la ligne scellée exige le code produit),
 * et en service : un article archivé ne descend sur aucune caisse, l'aperçu du studio ne doit donc pas le déverser.
 * ⚠️ ÉCRITE ICI parce que le serveur (aperçu du studio) et la caisse doivent déverser la MÊME liste : deux copies du
 *    filtre finiraient par en déverser deux. Le fragment est commun à PostgreSQL et à SQLite ; `alias` est la table
 *    `article` telle que la requête la nomme.
 */
export function quickSellableSql(alias: string): string {
  return `${alias}.is_active = 1 AND ${alias}.gencode IS NOT NULL AND ${alias}.gencode <> '' AND ${alias}.archived_at IS NULL`;
}

/**
 * LE NIVEAU D'UNE SOURCE TEL QU'IL SE DESSINE — rangé (`sourceLevel`), sans ce que la caisse ne dessine pas
 * (`drawnTiles`, et un article déversé qu'elle ne vend pas), puis borné (`boundSourceLevel`).
 * ⚠️ LA CAISSE ET L'APERÇU DU STUDIO LE LISENT PAR CETTE SEULE FONCTION : trois règles enchaînées à la main, dans un
 *    ordre ou un autre, finiraient par borner avant de filtrer sur une surface et après sur l'autre — et l'aperçu
 *    montrerait deux cents articles là où la caisse en montre cent quatre-vingt-dix-huit.
 */
export function drawnSourceLevel<T extends QuickTileNode>(
  own: readonly T[],
  poured: readonly string[],
  hidden: readonly string[],
  order: readonly string[],
  sellable: (articleId: string) => boolean,
  max: number = QUICK_POUR_MAX,
): { entries: QuickLevelEntry<T>[]; beyond: number } {
  const drawn = sourceLevel(drawnTiles(own, sellable), poured, hidden, order).filter((e) => e.articleId === null || sellable(e.articleId));
  return boundSourceLevel(drawn, max);
}

/**
 * LE NOMBRE QU'ANNONCE UNE SOURCE — ce que son niveau dessine et qu'on peut toucher, articles au-delà de la borne
 * COMPRIS : la tuile dit le compte réel, le niveau dit combien ne s'affichent pas.
 */
export function sourceCount<T extends QuickTileNode>(entries: readonly QuickLevelEntry<T>[], sellable: (articleId: string) => boolean): number {
  let n = 0;
  for (const e of entries) {
    if (e.articleId !== null) { if (sellable(e.articleId)) n += 1; continue; }
    if (drawnTiles([e.tile], sellable).length > 0 && e.tile.kind !== 'empty') n += 1;
  }
  return n;
}
